import React, { useRef, useEffect, useState, useCallback } from 'react';
import { X, RotateCcw, Check } from 'lucide-react';

interface SignatureCaptureProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (signatureDataUrl: string) => void;
  title: string;
}

const SignatureCapture: React.FC<SignatureCaptureProps> = ({
  isOpen,
  onClose,
  onSave,
  title,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;

    const rect = wrapper.getBoundingClientRect();
    const w = Math.max(320, Math.floor(rect.width));
    const h = Math.max(240, Math.floor(rect.height));

    canvas.width = w;
    canvas.height = h;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.clearRect(0, 0, w, h);
    setHasSignature(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const run = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(setupCanvas);
      });
    };

    run();
    const t = window.setTimeout(run, 50);

    const wrapper = wrapperRef.current;
    const ro =
      wrapper && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => run())
        : null;
    if (wrapper && ro) ro.observe(wrapper);

    window.addEventListener('resize', run);

    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', run);
      ro?.disconnect();
    };
  }, [isOpen, setupCanvas]);

  const getPoint = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ): { x: number; y: number } | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    if ('clientX' in e) {
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }
    return null;
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const p = getPoint(e);
    if (!p) return;

    if ('touches' in e) e.preventDefault();

    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const p = getPoint(e);
    if (!p) return;

    if ('touches' in e) e.preventDefault();

    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasSignature) return;

    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4">
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 p-4">
          <h2 className="text-xl font-bold text-gray-800">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 transition-colors hover:text-gray-700"
            aria-label="Cerrar"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto bg-gray-50 p-4">
          <div
            ref={wrapperRef}
            className="mx-auto w-full max-w-3xl rounded-lg border-2 border-gray-300 bg-white shadow-inner"
            style={{ height: 'min(55vh, 420px)', minHeight: 280 }}
          >
            <canvas
              ref={canvasRef}
              className="block h-full w-full cursor-crosshair touch-none"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
          <p className="mt-4 text-center text-sm text-gray-600">
            Dibuje en el recuadro con el mouse o el dedo. Si no ve el área en blanco, espere un segundo o
            redimensione la ventana.
          </p>
        </div>

        <div className="flex shrink-0 items-center justify-between border-t border-gray-200 bg-gray-50 p-4">
          <button
            type="button"
            onClick={clearSignature}
            className="btn-secondary flex items-center gap-2"
            disabled={!hasSignature}
          >
            <RotateCcw className="h-5 w-5" />
            Limpiar
          </button>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-6 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-100"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={saveSignature}
              disabled={!hasSignature}
              className="btn-primary flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Check className="h-5 w-5" />
              Guardar firma
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignatureCapture;
